import { isDefined } from "@clipboard-health/util-ts";
import { type HiringEvent } from "@src/appV2/redesign/PlacementCandidate/api/useGetHiringEvent";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format, parseISO } from "date-fns";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import { type InterviewAvailability } from "../../../api/useGetInterviewAvailability";
import { hasOnlyOpenHouseAvailable } from "../../../utils";

interface AlternativeAddressProps {
  hiringEvent?: HiringEvent;
  interviewSettings?: PlacementWithDetails["interviewSettings"];
  placementAddressFormatted?: string;
  interviewAvailability: InterviewAvailability;
  bookedInterview?: PlacementWithDetails["interviews"][number];
}

export interface AlternativeAddressData {
  coordinates: {
    longitude: number;
    latitude: number;
  };
  distance: number;
  formattedAddress: string;
  description: string;
}

interface AlternativeAddressResult {
  showAlternativeAddressInformation: boolean;
  showBookedInterviewLocation: boolean;
  alternativeAddressData?: AlternativeAddressData;
}

export function useAlternativeAddress(props: AlternativeAddressProps): AlternativeAddressResult {
  const {
    hiringEvent,
    interviewSettings,
    placementAddressFormatted,
    interviewAvailability,
    bookedInterview,
  } = props;

  if (!isDefined(placementAddressFormatted)) {
    return getBaseResult();
  }

  const hiringEventAddressEnabled = isDefined(hiringEvent);

  const interviewAddress = interviewSettings?.attributes.interviewAddress;
  const interviewAddressEnabled =
    isDefined(interviewAddress) && Boolean(interviewSettings?.attributes.scheduleInterviews);

  if (hiringEventAddressEnabled && interviewAddressEnabled) {
    return getAddressFromHiringEventAndInterviewAddress({
      hiringEvent,
      interviewAddress,
      placementAddressFormatted,
      interviewSettings,
      bookedInterview,
    });
  }

  if (hiringEventAddressEnabled) {
    return getAddressFromHiringEvent({
      hiringEvent,
      placementAddressFormatted,
      interviewAvailability,
      bookedInterview,
    });
  }

  if (interviewAddressEnabled) {
    return getAddressFromInterviewAddress({
      interviewAddress,
      placementAddressFormatted,
      interviewSettings,
    });
  }

  return getBaseResult();
}

function getBaseResult(): AlternativeAddressResult {
  return {
    showAlternativeAddressInformation: false,
    showBookedInterviewLocation: false,
  };
}

function hasBookedInterviewInHiringEvent(params: {
  bookedInterview?: PlacementWithDetails["interviews"][number];
  hiringEvent: HiringEvent;
}) {
  const { bookedInterview, hiringEvent } = params;
  return (
    isDefined(bookedInterview) &&
    isDefined(hiringEvent.fixedSchedule?.startDate) &&
    format(parseISO(hiringEvent.fixedSchedule.startDate), "yyyy-MM-dd") ===
      format(parseISO(bookedInterview.attributes.start), "yyyy-MM-dd")
  );
}

/**
 * Get the address when both hiring event and interview address are available
 * This is useful to get a unified address accross the modals and sections
 */
function getAddressFromHiringEventAndInterviewAddress(params: {
  hiringEvent: HiringEvent;
  interviewAddress: NonNullable<
    NonNullable<PlacementWithDetails["interviewSettings"]>["attributes"]["interviewAddress"]
  >;
  placementAddressFormatted: string;
  interviewSettings: PlacementWithDetails["interviewSettings"];
  bookedInterview?: PlacementWithDetails["interviews"][number];
}) {
  const {
    hiringEvent,
    interviewAddress,
    placementAddressFormatted,
    interviewSettings,
    bookedInterview,
  } = params;
  const hasDifferentAddress =
    hiringEvent?.address.formatted !== placementAddressFormatted ||
    interviewAddress.address.formatted !== placementAddressFormatted;
  const bookedInterviewInHiringEvent = hasBookedInterviewInHiringEvent({
    bookedInterview,
    hiringEvent,
  });
  return {
    showAlternativeAddressInformation: hasDifferentAddress,
    showBookedInterviewLocation: hasDifferentAddress,
    alternativeAddressData: {
      coordinates: bookedInterviewInHiringEvent
        ? hiringEvent.coordinates
        : interviewAddress.coordinates,
      distance: bookedInterviewInHiringEvent
        ? hiringEvent.distance ?? 0
        : interviewSettings?.attributes.interviewAddressDistance ?? 0,
      formattedAddress: bookedInterviewInHiringEvent
        ? hiringEvent.address.formatted
        : interviewAddress.address.formatted,
      description: bookedInterviewInHiringEvent
        ? hiringEvent.addressDescription ?? ""
        : interviewSettings?.attributes.interviewAddressDescription ?? "",
    },
  };
}

function getAddressFromHiringEvent(params: {
  hiringEvent: HiringEvent;
  placementAddressFormatted: string;
  interviewAvailability: InterviewAvailability;
  bookedInterview?: PlacementWithDetails["interviews"][number];
}): AlternativeAddressResult {
  const { hiringEvent, placementAddressFormatted, interviewAvailability, bookedInterview } = params;
  const hasDifferentAddress = hiringEvent.address.formatted !== placementAddressFormatted;
  const hasOnlyHiringEventAvailability = hasOnlyOpenHouseAvailable(interviewAvailability);
  const hasBookedInterviewInHiringEvent =
    isDefined(bookedInterview) &&
    isDefined(hiringEvent.fixedSchedule?.startDate) &&
    format(parseISO(hiringEvent.fixedSchedule.startDate), "yyyy-MM-dd") ===
      format(parseISO(bookedInterview.attributes.start), "yyyy-MM-dd");
  return {
    showAlternativeAddressInformation: hasDifferentAddress && hasOnlyHiringEventAvailability,
    showBookedInterviewLocation: hasDifferentAddress && hasBookedInterviewInHiringEvent,
    alternativeAddressData: {
      coordinates: hiringEvent.coordinates,
      distance: hiringEvent.distance ?? 0,
      formattedAddress: hiringEvent.address.formatted,
      description: hiringEvent.addressDescription ?? "",
    },
  };
}

function getAddressFromInterviewAddress(params: {
  interviewAddress: NonNullable<
    NonNullable<PlacementWithDetails["interviewSettings"]>["attributes"]["interviewAddress"]
  >;
  placementAddressFormatted: string;
  interviewSettings: PlacementWithDetails["interviewSettings"];
}): AlternativeAddressResult {
  const { interviewAddress, placementAddressFormatted, interviewSettings } = params;
  const hasDifferentAddress = interviewAddress.address.formatted !== placementAddressFormatted;
  return {
    showAlternativeAddressInformation: hasDifferentAddress,
    showBookedInterviewLocation: hasDifferentAddress,
    alternativeAddressData: {
      coordinates: interviewAddress.coordinates,
      distance: interviewSettings?.attributes.interviewAddressDistance ?? 0,
      formattedAddress: interviewAddress.address.formatted,
      description: interviewSettings?.attributes.interviewAddressDescription ?? "",
    },
  };
}
